import * as React from 'react';
import type { ButtonVariant } from '../Button/Button';
import { Tooltip } from '../Tooltip/Tooltip';
import { iconButton as iconButtonCls } from './IconButton.css';

export type IconButtonSize = 'sm' | 'md' | 'lg';

export interface IconButtonProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'children' | 'aria-label'
> {
  /** Required accessible label — icon-only buttons must have one. */
  'aria-label': string;
  /** The icon to render. Typically an <Icon /> component. */
  icon: React.ReactNode;
  variant?: ButtonVariant;
  size?: IconButtonSize;
  /**
   * Hover/focus tooltip. Defaults to the `aria-label` so every icon-only
   * control explains itself to mouse users; pass a shorter string to
   * override, or `false` to opt out (e.g. when the caller already wraps
   * it in its own `Tooltip`).
   */
  tooltip?: React.ReactNode;
  /** React 19 ref-as-prop. */
  ref?: React.Ref<HTMLButtonElement>;
}

/**
 * IconButton — square icon-only button. `aria-label` is required at the
 * type level since there is no visible label. Inherits the full
 * `<Button>` variant taxonomy (primary / secondary / ghost / etc.) and
 * the shared `theme.controlHeight` size scale (sm 30×30 / md 36×36 /
 * lg 44×44) so an IconButton and a Button at the same nominal size line
 * up in a toolbar.
 */
export function IconButton({
  icon,
  variant = 'ghost',
  size = 'md',
  type = 'button',
  ref,
  className,
  tooltip,
  ...rest
}: IconButtonProps) {
  const cls = [iconButtonCls({ variant, size }), className].filter(Boolean).join(' ');
  const button = (
    <button ref={ref} type={type} className={cls} {...rest}>
      {icon}
    </button>
  );
  const tip = tooltip === undefined ? rest['aria-label'] : tooltip;
  if (!tip) return button;
  // Keep the wrapper while disabled (just suppress the tip) so toggling
  // `disabled` doesn't remount the <button> and drop keyboard focus.
  return (
    <Tooltip content={tip} disabled={rest.disabled}>
      {button}
    </Tooltip>
  );
}
IconButton.displayName = 'IconButton';
